import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { SideDetailsLayout } from 'components/Layout/SideDetailsLayout';
import { TableSettings } from 'components/TableSettings';
import { TotalCount } from 'components/TotalCount';
import type { WorkOrderActKc2Data, WorkOrderStatusEnum } from 'gql/graphql';
import { ContractStatusEnum, SectionEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { Button, Grid, GridItem, Loader } from 'uikit';
import { getCheckedIdsArr } from 'utils';

import type { AgGridReact } from 'ag-grid-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';

import { EditRationaleDialog } from '../EditRationaleDialog';
import { useSeasons } from '../hooks/useSeasons';
import { WorkOrderOtherDelete } from '../WorkOrderOtherDelete';
import { WorkOrderOtherDeleteItem } from '../WorkOrderOtherDeleteItem';
import { WorkOrderSummary } from '../WorkOrderSummary';

import {
    useGetWorkOrderOtherDataGetter,
    useOthersActColumns,
    useOthersSeasonsColumns,
    useWorkOrderOtherColumns,
} from './hooks';
import { getOtherRowStyleRules } from './otherHelpers';
import { useLinkActs } from './useLinkActs';

interface IWorkOrderOtherTableProps {
    workOrderId: number;
    workOrderObjectId: number;
    section: SectionEnum;
    checkConstructionTypeHasActKC2: (arr: WorkOrderActKc2Data[] | undefined | null) => void;
    contractStatus?: ContractStatusEnum;
    workOrderStatus?: WorkOrderStatusEnum;
    reload?: any;
    isDisabledByProjectStatus?: boolean;
    workOrderHasInProgress?: boolean;
    costRounding: number;
}

export const WorkOrderOtherTable = forwardRef(
    (
        {
            workOrderId,
            workOrderObjectId,
            workOrderStatus,
            reload,
            contractStatus,
            isDisabledByProjectStatus,
            checkConstructionTypeHasActKC2,
            workOrderHasInProgress,
            costRounding,
        }: IWorkOrderOtherTableProps,
        outerRef,
    ) => {
        const ref = useRef<AgGridReact>(null);
        const navigate = useNavigate();

        const { id: projectId } = useParams();

        const isContractStatusWork = contractStatus === ContractStatusEnum.InImplementation;

        const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
        const [isSummaryDetailsVisible, setIsSummaryDetailsVisible] = useState(false);

        const onSummaryDetailsVisibilityChange = useCallback((isVisible) => {
            setIsSummaryDetailsVisible(isVisible);
        }, []);

        const handleChangeCheckbox = useCallback(
            (rowId: number) =>
                ({ checked }: { checked: boolean }) => {
                    setCheckedIds((ids) => ({ ...ids, [rowId]: checked }));
                },
            [],
        );

        const {
            tableItems,
            workOrderActsList,
            isLoading,
            fetchedItems,
            reFetchData,
            reFetchActsData,
            changeFilterHandler,
        } = useGetWorkOrderOtherDataGetter({
            workOrderId,
            workOrderRootObjectId: workOrderObjectId,
            reload,
            gridApiRef: ref.current,
        });

        const reloadDataHandler = useCallback(() => {
            reFetchData();
            setCheckedIds({});

            navigate('./', { replace: true, state: { reload: Math.random() } });
        }, [navigate, reFetchData]);

        const { refreshProject } = useProjectData(projectId, false);

        const reloadTree = useCallback(() => {
            reFetchActsData();
            reloadDataHandler();
            refreshProject();
        }, [reFetchActsData, refreshProject, reloadDataHandler]);

        useEffect(() => {
            checkConstructionTypeHasActKC2(workOrderActsList);
        }, [checkConstructionTypeHasActKC2, workOrderActsList]);

        const actColumns = useOthersActColumns(
            workOrderActsList,
            reloadTree,
            reloadDataHandler,
            contractStatus,
            workOrderStatus,
            isDisabledByProjectStatus,
            costRounding,
        );

        const [seasons] = useSeasons(workOrderId, true);
        const seasonColumns = useOthersSeasonsColumns({
            seasons: seasons ?? [],
            workOrderStatus,
            hasInProgress: workOrderHasInProgress,
            costRounding,
        });
        const linkedSeasons = useLinkActs({ seasonColumns, actColumns });

        const { fullSetColumns, fullSetSettingsColumns, handleTableSettingsConfirm, isColumnVisible } =
            useWorkOrderOtherColumns({
                workOrderStatus,
                isContractStatusWork,
                handleChangeCheckbox,
                checkedIds,
                isDisabledByProjectStatus,
                seasonAndLinkedActsColumns: linkedSeasons,
                hasActs: !!workOrderActsList?.length,
                workOrderId,
                workOrderObjectId,
                changeFilterHandler,
                gridApiRef: ref.current,
                costRounding,
            });

        const checkedIdsArr: number[] = getCheckedIdsArr(checkedIds);

        const handleClearChecked = useCallback(() => {
            setCheckedIds({});
        }, []);

        useImperativeHandle(outerRef, () => ({
            ...ref?.current,
        }));

        return (
            <>
                <Routes>
                    <Route
                        path="/deleteOther/:otherId"
                        element={<WorkOrderOtherDeleteItem workOrderStatus={workOrderStatus} />}
                    />
                    <Route
                        path="/editRationale/:otherId"
                        element={<EditRationaleDialog refreshData={reloadDataHandler} />}
                    />
                </Routes>

                {isLoading && <Loader />}
                <>
                    <Grid cols={2} className="TableSettings">
                        <GridItem>
                            {checkedIdsArr.length ? (
                                <div className="wrapperCount">
                                    <TotalCount
                                        totalCount={checkedIdsArr.length}
                                        forms={['строка', 'строки', 'строк']}
                                        prefix={checkedIdsArr.length > 1 ? 'Выбрано' : 'Выбрана'}
                                        className="totalCountOther"
                                    />
                                    <WorkOrderOtherDelete
                                        workOrderPriceIds={checkedIdsArr}
                                        onSuccess={reloadDataHandler}
                                        workOrderStatus={workOrderStatus}
                                    />
                                    <Button label="Сбросить" view="ghost" size="xs" onClick={handleClearChecked} />
                                </div>
                            ) : (
                                <TotalCount totalCount={fetchedItems} />
                            )}
                        </GridItem>
                        <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                            <TableSettings
                                columns={fullSetSettingsColumns}
                                onConfirm={handleTableSettingsConfirm}
                                isColumnVisible={isColumnVisible}
                                showPinColumn
                                tableApi={ref?.current}
                            />
                        </GridItem>
                    </Grid>
                    <SideDetailsLayout
                        onDetailsVisibilityChange={onSummaryDetailsVisibilityChange}
                        collapsedTitle="Показать итоги"
                        expandedTitle="Скрыть итоги"
                    >
                        <div className="ag-theme-alpine workOrderPrice workOrderOthers">
                            <AgGridTable
                                ref={ref}
                                rowData={tableItems}
                                columnDefs={fullSetColumns}
                                rowClassRules={getOtherRowStyleRules}
                                animateRows
                                suppressRowClickSelection
                                getRowId={(params) => params.data.id}
                                context={{
                                    tableUniqueId: `others_${workOrderObjectId}`,
                                }}
                            />
                        </div>
                        <WorkOrderSummary
                            section={SectionEnum.CustomerLimits}
                            chapter={null}
                            workOrderObjectId={workOrderObjectId}
                            workOrderPriceListData={workOrderActsList}
                            isVisible={isSummaryDetailsVisible}
                            costRounding={costRounding}
                        />
                    </SideDetailsLayout>
                </>
            </>
        );
    },
);

WorkOrderOtherTable.displayName = 'WorkOrderOtherTable';
